import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { Namespace } from '../../../../translation/namespace';
import AbstractFormsComboBox, {
  ComboBoxProps,
} from '../../../../components/molecules/Form/components/ComboBox/AbstractComboBox/AbstractFormsComboBox';
import { InstantSearchResponse } from './api/InstantSearchResponse';
import {
  AllContentResults,
  AllUserResult,
  ListItemEmpty,
  ListItemPage,
  ListItemPerson,
  Section,
  Selected,
  TopContentResult,
  TopUserResult,
  useComboBoxInput,
  usePopup,
} from '../../../../components/molecules/Form/components/ComboBox';
import getContentSuggestResult from './getContentSuggestResult';
import getFilteredContentSuggestResult from './getFilteredContentSuggestResult';
import getUserSuggestResult from './getUserSuggestResult';
import { isNullOrUndefined } from '../../../../lib/typeGuards';
import getSearchSuggestResult from './getSearchSuggestResult';
// eslint-disable-next-line max-len
import { SearchSuggestResult } from '../../../../components/molecules/Form/components/ComboBox/AbstractComboBox/types/SuggestResultTypes';
// eslint-disable-next-line max-len
import ListItemSearchSuggest from '../../../../components/molecules/Form/components/ComboBox/AbstractComboBox/ListItemSearchSuggest/ListItemSearchSuggest';
import { windowLogger } from '../../../../_lib/Logger/windowLogger';

// do not change order of fields! this defines the order in which they show up in the popup.
export const SECTION_KEYS = ['searchSuggestions', 'content', 'filteredContent', 'user'] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];
type InstantSearchResults = AllContentResults | TopContentResult | AllUserResult | TopUserResult | SearchSuggestResult;

type InstantSearchSections = Section<InstantSearchResults>[];
type InstantSearchComboBoxProps = ComboBoxProps & {
  suggestUrl: string;
  trackingUrl: string;
  onInput: React.ChangeEventHandler<HTMLInputElement>;
};

export default function InstantSearchComboBox({
  suggestUrl,
  onInput,
  validationMessage,
  trackingUrl,
  ...comboBoxProps
}: InstantSearchComboBoxProps) {
  const useComboBoxInputProps = {
    suggestUrl,
    threshold: 2,
    thresholdSingleCharacterExceptions: ['*'],
    onInput,
  };

  const { t } = useTranslation(Namespace.FormsComboBox);
  const navigate = useNavigate();
  const { closeResults, openResults, resultsOpen } = usePopup();
  const {
    clearInput,
    error,
    formInputRef,
    inputValue,
    loading,
    onInput: onInputEventHandler,
    resetError,
    response,
    isValidInputValue,
  } = useComboBoxInput<InstantSearchResponse>(useComboBoxInputProps);

  const containsAreaSearch: boolean = response ? 'filteredContent' in response : false;

  const transformSuggestResponse = (responseData: InstantSearchResponse | undefined) => {
    if (!responseData) {
      return undefined;
    }

    const toResults = (suggestResponse: InstantSearchResponse) => {
      return (key: SectionKey): InstantSearchSections => {
        switch (key) {
          case 'searchSuggestions':
            return getSearchSuggestResult(t)(key, containsAreaSearch, suggestResponse[key]);
          case 'content':
            return getContentSuggestResult(t)(key, containsAreaSearch, suggestResponse[key]);
          case 'filteredContent':
            return getFilteredContentSuggestResult(t)(suggestResponse[key]);
          case 'user':
            return getUserSuggestResult(t)(suggestResponse[key]);
          default:
            return [];
        }
      };
    };

    return SECTION_KEYS.flatMap(toResults(responseData));
  };
  const results = transformSuggestResponse(response);
  const getTrackingData = () => {
    // do not track * searches
    if (inputValue === '*') {
      return null;
    }
    return {
      searchQuery: inputValue,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      searchResultsCount: response ? response.content.allCount : 0,
      instantSearch: true,
      filteredSearch: false,
    };
  };

  const onSelection = ({ event, selectedItem }: Selected<InstantSearchResults>) => {
    // no event.preventDefault when selectedItem is type string
    // -> this results in submitting the form with the inputValue
    if (!selectedItem) {
      return;
    }
    if (typeof selectedItem !== 'string') {
      // on selection we save tracking info: See SON-13544
      if (selectedItem.type === 'topContentResult') {
        const trackingData = getTrackingData();
        if (trackingData) {
          axios
            .post(trackingUrl, trackingData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })
            .catch(function (err) {
              windowLogger.warn(err);
            });
        }
      }
      event.preventDefault();
      navigate(selectedItem.pageLink.url);
    }
  };

  const renderListItem = (item: InstantSearchResults): ReactNode => {
    switch (item.type) {
      case 'searchSuggestResult':
        return <ListItemSearchSuggest suggest={item} />;
      case 'allContentResult':
      case 'topContentResult':
      case 'allUserResult':
        return <ListItemPage page={item} link />;
      case 'topUserResult':
        return <ListItemPerson user={item} withPhone />;
      default:
        return null;
    }
  };

  const getValidationMessage = () => {
    if (isNullOrUndefined(inputValue)) {
      return validationMessage;
    }
    return isValidInputValue
      ? ''
      : t(`${Namespace.phrases}:validation.minLength`, { minLength: useComboBoxInputProps.threshold });
  };

  return (
    <AbstractFormsComboBox
      {...comboBoxProps}
      // popupProps
      openResults={openResults}
      closeResults={closeResults}
      resultsOpen={resultsOpen}
      // inputProps
      formInputRef={formInputRef}
      inputValue={inputValue}
      onInput={onInputEventHandler}
      clearInput={clearInput}
      error={error}
      resetError={resetError}
      loading={loading}
      // custom props
      results={results}
      validationMessage={getValidationMessage()}
      onSelection={onSelection}
      inputInContainer={false}
    >
      {item => (item ? renderListItem(item) : <ListItemEmpty />)}
    </AbstractFormsComboBox>
  );
}
